import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  IconArrowLeft, IconUsers, IconCirclesRelation, IconBook, IconWaveSine,
  IconCompass, IconAward, IconCreditCard, IconLoader2, IconTrendingUp,
  IconAdjustments,
} from "@tabler/icons-react";
import { api, formatApiError } from "@/lib/api";
import { useI18n } from "@/contexts/I18nContext";

export default function Tablero() {
  const { lang } = useI18n();
  const nav = useNavigate();
  const [kpi, setKpi] = useState(null);
  const [error, setError] = useState("");
  const [sim, setSim] = useState({ familias: 20, escuelas: 4, cursos_mes: 30, consultas_mes: 40, sellos_ano: 12 });
  const [simResult, setSimResult] = useState(null);
  const [simLoading, setSimLoading] = useState(false);

  useEffect(() => { (async () => {
    try { const { data } = await api.get("/admin/kpis"); setKpi(data); }
    catch (e) { setError(formatApiError(e)); }
  })(); }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setSimLoading(true);
      try {
        const { data } = await api.post("/admin/revenue-simulator", sim);
        if (!cancelled) setSimResult(data);
      } catch { /* noop */ }
      finally { if (!cancelled) setSimLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [sim]);

  return (
    <>
      <div className="subbar">
        <button className="btn-ghost btn text-[12px] px-2 py-1" onClick={() => nav("/app")} data-testid="tablero-back">
          <IconArrowLeft size={13} /> {lang === "es" ? "Volver" : "Back"}
        </button>
        <span className="t">{lang === "es" ? "Tablero · Control de la red" : "Dashboard · Network control"}</span>
      </div>

      <div className="wrap-app max-w-[1160px] py-10">
        {error && <p className="text-errtext text-[13px] mb-4">{error}</p>}
        {!kpi ? (
          <div className="card"><IconLoader2 className="animate-spin" /></div>
        ) : (
          <>
            {/* Top KPI band */}
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-8">
              <Kpi label={lang === "es" ? "Usuarios" : "Users"} value={kpi.users.total} icon={<IconUsers size={18} />} tint="teal" testid="kpi-users" />
              <Kpi label={lang === "es" ? "Casos activos" : "Active cases"} value={kpi.circulo.active_cases} icon={<IconCirclesRelation size={18} />} tint="teal" testid="kpi-cases" />
              <Kpi label={lang === "es" ? "Ingresos confirmados (USD)" : "Confirmed revenue (USD)"} value={`$${kpi.payments.revenue_paid_usd.toLocaleString()}`} icon={<IconCreditCard size={18} />} tint="honey" testid="kpi-revenue" />
              <Kpi label={lang === "es" ? "Sellos vigentes" : "Active seals"} value={kpi.sello.certified} icon={<IconAward size={18} />} tint="honey" testid="kpi-sellos" />
            </div>

            {/* Users by role */}
            <p className="eyebrow mb-3">{lang === "es" ? "Composición de la red" : "Network composition"}</p>
            <div className="card mb-8">
              <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
                {Object.entries(kpi.users.by_role).map(([role, n]) => (
                  <div key={role} className="text-center py-2 border border-line rounded-lg">
                    <p className="text-tert text-[11px] uppercase tracking-wider">{role}</p>
                    <p className="font-display text-[24px] font-medium mt-1">{n}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Module breakdown */}
            <p className="eyebrow mb-3">{lang === "es" ? "Actividad por módulo" : "Module activity"}</p>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3 mb-10">
              <ModuleCard
                icon={<IconCirclesRelation size={16} />} name="Círculo"
                items={[
                  { k: lang === "es" ? "Casos activos" : "Active cases", v: kpi.circulo.active_cases },
                  { k: lang === "es" ? "Bitácora (7d)" : "Log entries (7d)", v: kpi.circulo.week_bitacora },
                ]}
              />
              <ModuleCard
                icon={<IconBook size={16} />} name="Aula"
                items={[
                  { k: lang === "es" ? "Cursos publicados" : "Published", v: `${kpi.aula.published}/${kpi.aula.total}` },
                  { k: lang === "es" ? "Inscripciones" : "Enrollments", v: kpi.aula.enrollments },
                  { k: lang === "es" ? "Completados" : "Completions", v: kpi.aula.completions },
                ]}
              />
              <ModuleCard
                icon={<IconWaveSine size={16} />} name="Sintonía"
                items={[
                  { k: lang === "es" ? "Citas este mes" : "Appts this month", v: kpi.sintonia.this_month },
                  { k: lang === "es" ? "Próximas" : "Upcoming", v: kpi.sintonia.upcoming },
                ]}
              />
              <ModuleCard
                icon={<IconCompass size={16} />} name="Brújula"
                items={[
                  { k: lang === "es" ? "Escuelas" : "Schools", v: kpi.brujula.schools },
                  { k: lang === "es" ? "Índice medio" : "Avg index", v: `${kpi.brujula.avg_inclusion}%` },
                ]}
              />
              <ModuleCard
                icon={<IconAward size={16} />} name="Sello"
                items={[
                  { k: lang === "es" ? "En progreso" : "In progress", v: kpi.sello.in_progress },
                  { k: lang === "es" ? "Pendientes" : "Pending", v: kpi.sello.pending_review },
                  { k: lang === "es" ? "Certificadas" : "Certified", v: kpi.sello.certified },
                  { k: lang === "es" ? "Pool abierto" : "Open pool", v: kpi.sello.placements_open },
                ]}
              />
              <ModuleCard
                icon={<IconCreditCard size={16} />} name={lang === "es" ? "Ingresos" : "Payments"}
                items={[
                  { k: lang === "es" ? "Transacciones (mes)" : "Tx (this month)", v: kpi.payments.tx_this_month },
                  { k: lang === "es" ? "Pendientes" : "Pending", v: kpi.payments.tx_pending },
                  { k: lang === "es" ? "Suscripciones activas" : "Active entitlements", v: kpi.payments.entitlements_active },
                ]}
              />
            </div>

            {/* Revenue simulator */}
            <p className="eyebrow mb-3 flex items-center gap-2"><IconTrendingUp size={14} /> {lang === "es" ? "Simulador de ingresos" : "Revenue simulator"}</p>
            <div className="card grid lg:grid-cols-2 gap-8">
              <div className="space-y-4">
                <SimSlider label={lang === "es" ? "Familias · plan mensual ($20)" : "Families · monthly plan ($20)"} value={sim.familias} onChange={(v) => setSim({ ...sim, familias: v })} max={200} testid="sim-familias" />
                <SimSlider label={lang === "es" ? "Escuelas · plan anual ($427)" : "Schools · yearly plan ($427)"} value={sim.escuelas} onChange={(v) => setSim({ ...sim, escuelas: v })} max={50} testid="sim-escuelas" />
                <SimSlider label={lang === "es" ? "Ventas de curso Aula (mes) · $27" : "Aula course sales (month) · $27"} value={sim.cursos_mes} onChange={(v) => setSim({ ...sim, cursos_mes: v })} max={300} testid="sim-cursos" />
                <SimSlider label={lang === "es" ? "Consultas Sintonía (mes) · $33" : "Sintonía consults (month) · $33"} value={sim.consultas_mes} onChange={(v) => setSim({ ...sim, consultas_mes: v })} max={200} testid="sim-consultas" />
                <SimSlider label={lang === "es" ? "Certificaciones Sello (año) · $197" : "Sello certifications (year) · $197"} value={sim.sellos_ano} onChange={(v) => setSim({ ...sim, sellos_ano: v })} max={80} testid="sim-sellos" />
              </div>
              <div className="space-y-3">
                <div className="p-4 bg-teal-soft rounded-lg text-center">
                  <p className="lbl m-0">{lang === "es" ? "Ingreso mensual proyectado" : "Projected monthly revenue"}</p>
                  <p className="font-display text-[38px] font-medium text-teal-deep mt-1" data-testid="sim-monthly">
                    {simLoading ? "…" : `$${(simResult?.monthly || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
                  </p>
                  <p className="text-tert text-[11.5px]">
                    {lang === "es" ? "Anualizado" : "Annualized"}: ${(simResult?.annual || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </p>
                </div>
                <div className="space-y-1.5">
                  <p className="lbl m-0 flex items-center gap-2"><IconAdjustments size={12} /> {lang === "es" ? "Desglose" : "Breakdown"}</p>
                  {(simResult?.breakdown || []).map((row) => (
                    <div key={row.stream} className="flex items-center justify-between text-[12.5px] border-b border-line py-1">
                      <span>{row.stream}</span>
                      <span className="font-mono">${row.monthly.toFixed(2)}/mo · ${row.annual.toFixed(2)}/yr</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </>
  );
}

function Kpi({ label, value, icon, tint, testid }) {
  const bg = tint === "honey" ? "bg-honey-soft text-honey-deep" : "bg-teal-soft text-teal-deep";
  return (
    <div className="card" data-testid={testid}>
      <div className="flex items-center justify-between">
        <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${bg}`}>{icon}</div>
      </div>
      <p className="font-display text-[26px] font-medium mt-2">{value}</p>
      <p className="text-tert text-[11.5px] mt-0.5">{label}</p>
    </div>
  );
}

function ModuleCard({ icon, name, items }) {
  return (
    <div className="card">
      <div className="flex items-center gap-2 mb-3">
        <div className="w-8 h-8 rounded-lg bg-teal-soft text-teal-deep flex items-center justify-center">{icon}</div>
        <p className="font-display text-[15px] font-medium">{name}</p>
      </div>
      <div className="space-y-1">
        {items.map((it) => (
          <div key={it.k} className="flex items-center justify-between text-[12.5px]">
            <span className="text-muted">{it.k}</span>
            <span className="font-medium">{it.v}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function SimSlider({ label, value, onChange, max, testid }) {
  return (
    <div>
      <div className="flex items-center justify-between text-[12px] mb-1">
        <span>{label}</span>
        <span className="font-mono font-medium" data-testid={`${testid}-value`}>{value}</span>
      </div>
      <input
        type="range"
        min="0"
        max={max}
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        className="w-full"
        data-testid={testid}
      />
    </div>
  );
}
